<template>
    <div class="message-box dialog-mask terms-of-service-dialog">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">{{ $t('error.modal.20256.title') }}</h4>
                </div>
                <div class="modal-body">
                    <p v-html="$t('error.modal.20256.desc')" />
                    <br />
                    <p v-if="parentEmail" v-html="$t('error.modal.20256.parent-email', { email: parentEmail })" />

                    <div class="input-container">
                        <authCodeInput
                            v-model="$v.inputCode.$model"
                            autofocus
                            :has-error="submitted && (!!errorText || $v.inputCode.$error)"
                            :error-text="errorText"
                            @fulfill="verifyCode"
                        />

                        <div class="clearfix"></div>
                    </div>
                </div>
                <div class="modal-footer">
                    <ModalMultiButtons
                        :btn-primary-text="$t('error.modal.20256.button')"
                        :btn-secondary-text="$t('cancel')"
                        :primary-button-click="verifyCode"
                        :secondary-button-click="clickCancel"
                    ></ModalMultiButtons>
                    <small
                        ><resendLink
                            :resend-link-text="$t('error.modal.20256.resend-code')"
                            :resend-loading="resendLoading"
                            @click="resendCode"
                    /></small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ModalMultiButtons } from '@arena/toolkit';
import { required, minLength } from 'vuelidate/lib/validators';
import modalEscMixin from '@/mixins/modalEscMixin';
import autoSubmit from '@/mixins/autoSubmit';
import authCodeInput from '@/components/global/auth-code-input';
import resendLink from '@/components/resend-link';

export default {
    components: {
        ModalMultiButtons,
        authCodeInput,
        resendLink,
    },
    mixins: [modalEscMixin, autoSubmit],
    props: {
        parentEmail: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            errorCode: '',
            errorText: '',
            inputCode: '',
            buttonLoading: false,
            resendLoading: false,
            submitted: false,
        };
    },
    mounted() {
        dataLayer.push({
            event: 'SignupLoginErrorView',
            errorName: 'Account Reconsent',
        });
    },
    validations() {
        return {
            inputCode: {
                required,
                minLength: minLength(6),
            },
        };
    },
    created() {
        this.handleEscCallbackToMixin = this.clickCancel;
    },
    methods: {
        async clickCancel() {
            dataLayer.push({
                event: 'Closed Terms of Service Modal',
                'Parent Email': this.parentEmail,
            });
            this.$close();
        },
        setError(error) {
            this.buttonLoading = false;
            this.errorCode = error;
            this.submitted = false;
        },
        submit() {
            this.buttonLoading = true;
            this.errorCode = '';
        },
        verifyCode() {
            this.$v.$touch();
            this.submitted = true;
            const code = this.inputCode;
            if (code.length !== 6) {
                this.errorText = this.$t('error.input.verificationCode');
                return;
            }
            this.submit();
            this.$accountAPI
                .post('/no-auth/terms/consent-by-parent', {
                    consentLocation: this.$store.state.isApplication ? 'LAUNCHER' : 'WEB',
                    verificationCode: code,
                })
                .then(() => {
                    this.$store.dispatch('login/loginComplete');
                    this.$close();
                })
                .catch(({ errorCode }) => {
                    console.error('Error during verification:', errorCode, this.submitted, this.inputCode);
                    if (errorCode === 20068) {
                        this.errorText = this.$t('error.form.invalid-code');
                        return;
                    }
                    this.setError(this.errorCode);
                    this.$close();
                });
        },
        sendCode() {
            return this.$accountAPI.post('/no-auth/terms/request-code-to-parent').catch(({ errorCode }) => {
                this.errorCode = errorCode;
                throw errorCode;
            });
        },
        resendCode() {
            this.errorCode = '';
            this.resendLoading = true;
            this.sendCode()
                .then(() => {
                    this.resendLoading = false;
                    this.trackOtherLinkClick(this.$t('resend', 'en'));
                })
                .catch(() => {
                    this.resendLoading = false;
                });
        },
        trackOtherLinkClick(name) {
            dataLayer.push({
                event: 'accountTOUService_OtherLinkClick',
                'AR Link Name': name,
            });
        },
    },
};
</script>

<style lang="less" scoped></style>
